import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSearchData, useSearchResult } from 'app/services/search-api';
import { ISearchRequestDto, SearchOption, SearchPaginatedDetailDto, SearchResultDocumentResponse, SuggestResponse } from 'generated/search-backend';
import useStore, { IStore } from 'app/store';
import voiceIcon from 'app/static/icons/microVoice.svg';
import { useSpeechToTextToken } from 'app/services/search-api';

import { isUser } from 'app/utils/authUtilities';
import { escapeToSafeCharacters, formatSearchIntoRequestData, formatSearchSuggestionIntoRequestData, getDate, keyPress } from 'app/components/helpers';

import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import { Languages, StringConstants } from 'app/utils/constants';
import { debounce } from 'lodash';
import { RequestDto, UserDto } from 'generated/request-backend';

export interface SearchResult {
    name: string;
    link: string;
}

interface IProps {
    isAssociatingParent: boolean;
    setFilteredResult: (result: RequestDto[]) => void;
    refetchChildList: Function;
}

const AssociationSearchBar: React.FC<IProps> = ({ isAssociatingParent, setFilteredResult, refetchChildList }) => {
    const userDetails: UserDto = useStore((state: IStore) => state.userDetails as UserDto);

    const [isSearchResultShown, ] = useState(false);

    const location = useLocation();
    const { pathname } = location;

    const [searchText, setSearchText] = useState('');
    const [searchResult, ] = useState([]);

    const setUserSearchValue = useStore((state: IStore) => state.setUserSearchValue);

    const { mutate: suggestData, data: suggestResultData, isSuccess: isSuccessSuggest } = useSearchData();
    const { mutate: searchData, data: searchResultData, isSuccess: isSuccessSearch } = useSearchResult();

    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const { refetch: getSpeechToTextToken } = useSpeechToTextToken();
    const [isRecording, setIsRecording] = useState(false);
    const [recognizer, setRecognizer] = useState(null);

    //voice to text
    const sttFromMic = async (action: Boolean) => {
        if (action) {
            try {
                let token = JSON.parse(sessionStorage.getItem('speech-token'));
                if (!(token && token.Token)) {
                    await getSpeechToTextToken();
                    token = JSON.parse(sessionStorage.getItem('speech-token'));
                }
                const speechConfig = speechSdk.SpeechConfig.fromAuthorizationToken(token?.Token ?? '', token?.Region ?? '');
                speechConfig.speechRecognitionLanguage = Languages.EN_US;
                speechConfig.setServiceProperty('punctuation', 'explicit', speechSdk.ServicePropertyChannel.UriQueryParameter);
                const audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();

                let newRecognizer = new speechSdk.SpeechRecognizer(speechConfig, audioConfig);
                setRecognizer(newRecognizer);
                setIsRecording(true);

                newRecognizer.recognized = function (s: speechSdk.Recognizer, e: speechSdk.SpeechRecognitionEventArgs) {
                    let displayText;

                    if (e.result.text) {
                        displayText = `${e.result.text}`;
                        displayText = displayText.replace('SKP ', 'SKP-');

                        setSearchText(displayText);
                        setUserSearchValue(displayText);
                        setIsRecording(false);
                        searchFieldRef.current.focus();
                        suggestData({
                            term: displayText,
                            searchOption: SearchOption.HelpRequest,
                            isAdminSearch:
                                !isUser(userDetails?.userRoles) &&
                                (pathname.includes('manage-requests') ||
                                    (!isUser(userDetails?.userRoles) && pathname === StringConstants.FORWARDSLASH) ||
                                    pathname.includes('report') ||
                                    pathname.includes('request-detail') ||
                                    pathname.includes('visual-flow')),
                            isParentChildSearch: true
                        });

                        newRecognizer.stopContinuousRecognitionAsync(
                            () => {},
                            (e: string) => {}
                        );
                    }
                };

                newRecognizer.startContinuousRecognitionAsync(() => {
                    setTimeout(() => {
                        newRecognizer.stopContinuousRecognitionAsync(
                            () => {},
                            (e: string) => {}
                        );
                        setIsRecording(false);
                    }, 20000);
                });
            } catch (e) {
                console.error('Speech recognition failed to initialize:', e);
                setIsRecording(false);
            }
        }

        if (!action) {
            recognizer.stopContinuousRecognitionAsync(
                (result: string) => {},
                (e: string) => {}
            );
        }
    };

    const voiceStartClick = () => {
        sttFromMic(true);
    };

    useEffect(() => {
        if (isSuccessSearch) {
            var filteredResults = searchResultData?.searchResult?.items?.filter((result: SearchResultDocumentResponse) => {
                if (isAssociatingParent) {
                    return result.document.isParent === true;
                } 
                return (result.document.parentRequestId === null || result.document.parentRequestId === "");
            }).map((item: SearchResultDocumentResponse) => item.document);

            setFilteredResult(formatSearchIntoRequestData(filteredResults, isAssociatingParent))
        }
    }, [isSuccessSearch]);

    useEffect(() => {
        if (isSuccessSuggest) {
            var filteredResults = suggestResultData?.filter((result: SuggestResponse) => {
                if (isAssociatingParent) {
                    return result.document.isParent === true;
                } 

                return result.document.parentRequestId === null;
            }).map((item: SuggestResponse) => item.document);

            setFilteredResult(formatSearchSuggestionIntoRequestData(filteredResults, isAssociatingParent))
        }
    }, [isSuccessSuggest]);

    const onFocus = () => {
        setIsSearchFocused(true);
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        
        if (event.target.value.length <= 2) {
            refetchChildList();
        } else {
            setUserSearchValue(event.target.value);
            if (event.target.value.trim()) {
                let payload : ISearchRequestDto = {
                    searchTerm: escapeToSafeCharacters(event.target.value),
                    pageIndex: 1,
                    filter: getDate('All Dates'),
                    pageSize: 30,
                    searchOption: SearchOption.HelpRequest,
                    isDateRangeCountRequired: true,
                    searchPaginatedDetails: [] as SearchPaginatedDetailDto[],
                    isAdminSearch: true
                };

                searchData(payload);
            }
        }
    };

    const debouncedFunction = useCallback(debounce(onChange, 300), []);
    
    const debouncedChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedFunction(event);
        setSearchText(event.target.value);
    };

    const searchContainer = useRef(null);
    const searchFieldRef = useRef(null);

    return (
        <>
            <div className="search-box-top" role="searchbox" aria-label="search-requests-field" ref={searchContainer}>
                {!isRecording ? (
                    <img
                        tabIndex={0}
                        src={voiceIcon}
                        alt="Voice"
                        className="voice-icon"
                        onKeyPress={(e) => keyPress(e, voiceStartClick)}
                        onClick={() => {
                            voiceStartClick();
                        }}
                    />
                ) : (
                    ''
                )}
                {isRecording ? (
                    <div
                        className="recording-row recording-position"
                        onKeyPress={(e) =>
                            keyPress(e, () => {
                                setIsRecording(false);
                                sttFromMic(false);
                            })
                        }
                        tabIndex={0}
                        onClick={() => {
                            setIsRecording(false);
                            sttFromMic(false);
                        }}>
                        <div className="first-line"></div>
                        <div className="second-line"></div>
                        <div className="third-line"></div>
                        <div className="fourth-line"></div>
                        <div className="fifth-line"></div>
                    </div>
                ) : (
                    ''
                )}
                <input
                    tabIndex={0}
                    className={`search-input ${
                        isSearchResultShown ? 'search-input-focus' : searchResult?.length > 0 ? 'show-suggestions ' : ''
                    } ${searchText?.length > 3 ? 'search-input-grown' : ''}  ${
                        isSearchFocused ? 'search-animation' : 'search-shrink-animation'
                    }`}
                    onChange={debouncedChangeHandler}
                    onFocus={() => onFocus()}
                    type="text"
                    value={searchText}
                    data-testid="search-text"
                    role="searchbox"
                    ref={searchFieldRef}
                    placeholder={'Search'}
                />
            </div>
        </>
    );
};

export default AssociationSearchBar;
